import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Search, Send, UserSearch } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, setPageTitle } from "../lib/format";

const STATUSES = ["", "open", "in_progress", "waiting_user", "escalated", "resolved", "closed"];
const PATCH_STATUSES = ["open", "in_progress", "waiting_user", "escalated", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

// Read-only verification visibility for support (§14): queue + open reports.
// Support can SEE verification state; approve/reject/resolve stay admin-only.
function VerificationQueue() {
  const [queue, setQueue] = useState(null);
  const [reports, setReports] = useState(null);
  useEffect(() => {
    api.supportListingQueue().then(setQueue).catch(() => setQueue([]));
    api.supportReports().then(setReports).catch(() => setReports([]));
  }, []);
  return (
    <div className="mt-8 grid lg:grid-cols-2 gap-6" data-testid="support-verification-queue">
      <div className="card-dark p-5">
        <h2 className="font-bold text-sm">Listings under review <span className="text-zinc-500 font-normal">(read-only)</span></h2>
        {queue === null ? <PageLoading label="Loading…" /> : queue.length === 0 ? (
          <p className="text-xs text-zinc-600 mt-3">No listings waiting for review.</p>
        ) : (
          <div className="mt-3 space-y-2.5 max-h-64 overflow-auto">
            {queue.map((s) => (
              <div key={s.spot_id} className="rounded-lg border border-[#27272A] p-3" data-testid={`support-queue-${s.spot_id}`}>
                <p className="text-sm font-medium truncate">{s.title}</p>
                <p className="text-[0.7rem] text-zinc-500 mt-1">
                  <span className="text-amber-400">{s.status}</span> · attestation {s.trust?.host_attestation ? "yes" : "no"} · authz {s.trust?.authorization_type || "—"} · proximity {s.trust?.location_proximity_verified ? "verified" : "—"}
                  {s.review_reason ? ` · ${s.review_reason}` : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="card-dark p-5">
        <h2 className="font-bold text-sm">Open unauthorized-listing reports <span className="text-zinc-500 font-normal">(read-only)</span></h2>
        {reports === null ? <PageLoading label="Loading…" /> : reports.length === 0 ? (
          <p className="text-xs text-zinc-600 mt-3">No open reports.</p>
        ) : (
          <div className="mt-3 space-y-2.5 max-h-64 overflow-auto">
            {reports.map((r) => (
              <div key={r.report_id} className="rounded-lg border border-[#27272A] p-3" data-testid={`support-report-${r.report_id}`}>
                <p className="text-sm font-medium capitalize">{r.reporter_type} report · listing {r.spot_id}</p>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{r.details}</p>
                <p className="text-[0.7rem] text-zinc-600 mt-1">{fmtDateTime(r.created_at)} · escalate to an admin to resolve</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SupportConsole() {  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.supportDashboard().then(setKpis).catch((e) => setError(e.message));
    api.supportAllTickets({ status: statusFilter || undefined, category: categoryFilter || undefined, role: roleFilter || undefined }).then(setTickets).catch(() => setTickets([]));
  };
  useEffect(() => { setPageTitle("Support Console"); load(); }, [statusFilter, categoryFilter, roleFilter]); // eslint-disable-line

  const CATEGORIES = [["", "All categories"], ["booking", "Booking"], ["payment", "Payment"], ["listing", "Listing"], ["account", "Account"], ["safety", "Safety"], ["other", "Other"]];
  const q = search.trim().toLowerCase();
  const visibleTickets = (tickets || []).filter((t) => !q || [t.ticket_id, t.user_email, t.subject, t.booking_id, t.spot_id].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));

  const openTicket = async (t) => {
    try { setActive(await api.getTicket(t.ticket_id)); } catch (e) { toast.error(e.message); }
  };

  const patch = async (body) => {
    try {
      await api.patchTicket(active.ticket_id, body);
      toast.success("Ticket updated");
      setActive(await api.getTicket(active.ticket_id));
      load();
    } catch (e) { toast.error(e.message); }
  };

  const reply = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try { await api.replyTicket(active.ticket_id, draft.trim()); setDraft(""); setActive(await api.getTicket(active.ticket_id)); load(); }
    catch (err) { toast.error(err.message); }
  };

  const searchUsers = async (q) => {
    setUserQuery(q);
    if (q.trim().length < 2) { setUserResults([]); return; }
    try { setUserResults(await api.supportSearchUsers(q.trim())); } catch { setUserResults([]); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="support-console">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Support</p>
        <h1 className="type-display mt-3">Support Console</h1>
        <p className="mt-3 text-sm text-zinc-500">Signed in as {user?.email} · support role (no admin privileges)</p>

        {error ? <div className="mt-8"><ErrorState body={error} onRetry={load} /></div> : (
          <>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="support-kpis">
              {[["Open", kpis?.open, "kpi-open"], ["Escalated", kpis?.escalated, "kpi-escalated"], ["Resolved today", kpis?.resolved_today, "kpi-resolved"], ["Urgent", kpis?.urgent, "kpi-urgent"]].map(([l, v, tid]) => (
                <div key={tid} className="card-dark p-5" data-testid={tid}>
                  <p className="font-display font-extrabold text-2xl">{v ?? "—"}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{l}</p>
                </div>
              ))}
            </div>

            {/* Verification queue — read-only for support (admin actions stay admin-only) */}
            <VerificationQueue />

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
              <div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="relative flex-1 min-w-48">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                    <input data-testid="support-ticket-search" value={search} onChange={(e) => setSearch(e.target.value)}
                           className="input-dark !h-9 !pl-9 text-xs" placeholder="Search ticket, user, email, booking or listing…" aria-label="Search tickets" />
                  </div>
                  <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="support-filter-category"
                          className="input-dark !h-9 text-xs !w-auto" aria-label="Category filter">
                    {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="support-filter-role"
                          className="input-dark !h-9 text-xs !w-auto" aria-label="Role filter">
                    {[["", "All users"], ["driver", "Drivers"], ["host", "Hosts"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 overflow-auto mb-4" role="tablist" aria-label="Ticket status filter">
                  {STATUSES.map((s) => (
                    <button key={s || "all"} role="tab" aria-selected={statusFilter === s} data-testid={`support-filter-${s || "all"}`} onClick={() => setStatusFilter(s)}
                            className={`px-3.5 py-2 rounded-full text-xs font-semibold border whitespace-nowrap transition-colors ${statusFilter === s ? "bg-[#E50914] border-[#E50914]" : "bg-[#1F2328] border-[#27272A] text-zinc-300"}`}>
                      {s ? s.replace(/_/g, " ") : "All"}
                    </button>
                  ))}
                </div>
                {tickets === null ? <PageLoading label="Loading tickets…" /> : visibleTickets.length === 0 ? (
                  <EmptyState title="No tickets" body="Tickets matching this filter will appear here." />
                ) : (
                  <div className="space-y-2.5 max-h-[34rem] overflow-auto" data-testid="support-tickets-list">
                    {visibleTickets.map((t) => (
                      <button key={t.ticket_id} onClick={() => openTicket(t)} data-testid={`support-ticket-${t.ticket_id}`}
                              className="w-full text-left card-dark card-dark-hover p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-semibold text-sm truncate">{t.subject || "(no subject)"}</p>
                          <StatusBadge value={t.status} />
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">{t.user_email} · {t.category || "general"} · {fmtDateTime(t.created_at)}</p>
                      </button>
                    ))}
                  </div>
                )}

                <div className="card-dark p-5 mt-6" data-testid="support-user-search">
                  <h2 className="font-bold text-sm flex items-center gap-2"><UserSearch className="w-4 h-4 text-[#FF3B45]" /> User search</h2>
                  <div className="relative mt-3">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input data-testid="support-user-search-input" value={userQuery} onChange={(e) => searchUsers(e.target.value)}
                           className="input-dark !pl-11" placeholder="Search by email, name, or user ID" aria-label="Search users" />
                  </div>
                  {userResults.length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="support-user-results">
                      {userResults.map((u) => (
                        <div key={u.user_id} className="p-3 rounded-xl bg-[#080B0D] border border-[#27272A] text-xs flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">{u.name}</p>
                            <p className="text-zinc-500 truncate">{u.email} · {u.user_id}</p>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <StatusBadge value={u.suspended ? "restricted" : (u.role || "normal")} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                {!active ? (
                  <EmptyState title="Select a ticket" body="Open a ticket from the list to see the conversation, change status, and reply." />
                ) : (
                  <div className="card-dark p-6" data-testid="support-ticket-detail">
                    <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#27272A]">
                      <div className="min-w-0">
                        <h2 className="font-bold truncate">{active.subject}</h2>
                        <p className="text-xs text-zinc-500 mt-1">{active.user_email} · {active.ticket_id}</p>
                      </div>
                      <div className="flex gap-2"><StatusBadge value={active.priority} /><StatusBadge value={active.status} /></div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div>
                        <label htmlFor="sc-status" className="label-dark">Status</label>
                        <select id="sc-status" data-testid="support-status-select" value={active.status} onChange={(e) => patch({ status: e.target.value })} className="input-dark !h-10 text-xs">
                          {PATCH_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="sc-priority" className="label-dark">Priority</label>
                        <select id="sc-priority" data-testid="support-priority-select" value={active.priority || "normal"} onChange={(e) => patch({ priority: e.target.value })} className="input-dark !h-10 text-xs">
                          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </div>
                    <button onClick={() => patch({ assigned_to: user.user_id })} data-testid="support-assign-me" className="btn-secondary w-full mt-3 !py-2 text-xs">
                      Assign to me
                    </button>

                    <div className="mt-4 space-y-2.5 max-h-56 overflow-auto" data-testid="support-ticket-messages">
                      {(active.messages || []).map((m) => (
                        <div key={m.message_id} className={`px-4 py-2.5 rounded-2xl text-sm max-w-[90%] ${m.sender_role === "support" ? "ml-auto bg-[#E50914]/15 border border-[#E50914]/25" : "bg-[#080B0D] border border-[#27272A]"}`}>
                          <p className="text-[10px] opacity-60 mb-0.5">{m.sender_name} · {fmtDateTime(m.created_at)}</p>{m.text}
                        </div>
                      ))}
                    </div>
                    <form onSubmit={reply} className="mt-3 flex gap-2">
                      <input data-testid="support-reply-input" value={draft} onChange={(e) => setDraft(e.target.value)} className="input-dark flex-1" placeholder="Reply to user…" aria-label="Reply to user" />
                      <button type="submit" data-testid="support-reply-btn" className="btn-primary !px-4" aria-label="Send reply"><Send className="w-4 h-4" /></button>
                    </form>

                    <div className="mt-5 pt-4 border-t border-[#27272A]">
                      <label htmlFor="sc-note" className="label-dark">Internal note (support/admin only)</label>
                      <div className="flex gap-2">
                        <input id="sc-note" data-testid="support-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="input-dark flex-1" placeholder="Note for the team…" />
                        <button onClick={() => { if (note.trim()) { patch({ internal_note: note.trim() }); setNote(""); } }} data-testid="support-note-btn" className="btn-secondary !px-4 text-xs">Add</button>
                      </div>
                      {(active.internal_notes || []).length > 0 && (
                        <div className="mt-3 space-y-2" data-testid="support-internal-notes">
                          {active.internal_notes.map((n) => (
                            <p key={n.note_id} className="text-xs text-zinc-500 p-2.5 rounded-lg bg-[#080B0D] border border-[#27272A]">
                              <span className="text-zinc-300 font-semibold">{n.author_email}:</span> {n.text}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
